import { createSelector } from "reselect";

import isValidLongLat from "@mapmycustomers/shared/util/geo/isValidLongLat";

import {
  LEAD_FINDER_DEFAULT_DISTANCE_KM,
  LEAD_FINDER_DEFAULT_DISTANCE_MILES,
} from "@app/component/leadFinder/utils/consts";
import localSettings from "@app/config/LocalSettings";
import { getUserMetaData } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";

const leadFinderState = (state: RootState) => state.component.leadFinder;

export const getLeadFinderSettings = createSelector(getUserMetaData, (metaData) => {
  const settings = metaData.leadFinderSettings;

  const doesUseSiUnits = localSettings.doesUseSiUnits();
  const defaultSearchDistance = doesUseSiUnits
    ? LEAD_FINDER_DEFAULT_DISTANCE_KM
    : LEAD_FINDER_DEFAULT_DISTANCE_MILES;

  if (!settings) {
    return {
      customDistances: [],
      recentSearches: [],
      savedSearches: [],
      searchDistance: defaultSearchDistance,
    };
  }

  return {
    customDistances: Array.isArray(settings.customDistances) ? settings.customDistances : [],
    recentSearches: Array.isArray(settings.recentSearches) ? settings.recentSearches : [],
    savedSearches: Array.isArray(settings.savedSearches) ? settings.savedSearches : [],
    searchDistance:
      !settings.searchDistance || isNaN(settings.searchDistance)
        ? defaultSearchDistance
        : Math.max(Math.min(settings.searchDistance, defaultSearchDistance), 0.1),
  };
});

export const isAddRecordsVisible = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.addRecords.visible
);

export const isAddRecordsResultLoading = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.addRecords.loading
);

export const areRoutesLoading = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.addRecords.routesLoading
);

export const isResultModalVisible = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.addRecords.resultsVisible
);

export const isConfigurationVisible = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.configurationVisible
);

export const areDuplicatesVisible = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.duplicates.visible
);

export const getDuplicatesRecords = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.duplicates.records
);

export const hasCenterPoint = createSelector(leadFinderState, (leadFinder) =>
  isValidLongLat(leadFinder.centerPoint)
);

export const getCenterPoint = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.centerPoint
);

export const getAddRecordsEntityType = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.addRecords.entityType
);

export const getAddRecordsAvailableRoutes = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.addRecords.availableRoutes
);

export const getAddRecordsProgress = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.addRecords.progress
);

export const getLeadFinderProcessing = createSelector(
  leadFinderState,
  (leadFinder) => !!leadFinder.processing
);

export const getLeadFinderQuery = createSelector(leadFinderState, (leadFinder) => leadFinder.query);

export const getLeadFinderAddress = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.query.address
);

export const getLeadFinderEntity = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.query.entity
);

export const getLeadFinderLocation = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.query.location
);

export const getLeadFinderMode = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.query.mode
);

export const getLeadFinderSearch = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.query.search
);

export const getLeadFinderCurrentBounds = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.currentBounds
);

export const getLeadFinderRecentSearches = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.recentSearches
);
export const getLeadFinderSavedSearches = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.savedSearches
);
export const isSavedSearchesModalVisible = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.savedSearchesModal
);
export const getLeadFinderCustomDistances = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.customDistances
);
export const getLeadFinderSearchDistance = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.searchDistance
);

export const getLeadFinderRecords = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.results.records
);

export const getLeadFinderOtherPins = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.otherPins
);

export const getPrimaryRecord = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.duplicates.primaryRecord
);

export const isHiddenLeadsVisible = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.hiddenLeads.visible
);

export const isPotentialDuplicateLeadsVisible = createSelector(
  leadFinderState,
  (leadFinder) => leadFinder.potentialDuplicateLeads.visible
);

export const getFilteredLeadFinderRecords = createSelector(
  getLeadFinderRecords,
  isHiddenLeadsVisible,
  isPotentialDuplicateLeadsVisible,
  (records, hiddenLeadsVisible, potentialDuplicateLeadsVisible) => {
    const filterHiddenRecords = hiddenLeadsVisible
      ? records
      : records.filter(({ blackListed }) => !blackListed);
    return potentialDuplicateLeadsVisible
      ? filterHiddenRecords
      : filterHiddenRecords.filter(
          ({ blackListed, mmcRecords }) => !mmcRecords?.length || blackListed
        );
  }
);

export const getFilteredLeadFinderRecordsCount = createSelector(
  getFilteredLeadFinderRecords,
  (records) => records.length
);

export const getLeadFinderConfiguration = createSelector(leadFinderState, (leadFinder) => ({
  customDistances: leadFinder.customDistances ?? [],
  recentSearches: leadFinder.recentSearches ?? [],
  savedSearches: leadFinder.savedSearches ?? [],
  searchDistance: leadFinder.searchDistance,
}));

export const getLeadAnnotation = createSelector(leadFinderState, ({ annotation }) => annotation);
